import React, { useState } from 'react';
import { BarChart3, TrendingUp, Calendar, Award } from 'lucide-react';

interface AnalyticsData {
  week: string;
  workouts: number;
  duration: number;
  calories: number;
}

const WeeklyAnalyticsPanel: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month'>('week');

  const weeklyData: AnalyticsData[] = [
    { week: 'Week 1', workouts: 3, duration: 135, calories: 1250 },
    { week: 'Week 2', workouts: 4, duration: 180, calories: 1680 },
    { week: 'Week 3', workouts: 5, duration: 225, calories: 2100 },
    { week: 'Week 4', workouts: 4, duration: 200, calories: 1850 },
    { week: 'Week 5', workouts: 5, duration: 240, calories: 2200 },
    { week: 'Week 6', workouts: 3, duration: 150, calories: 1400 },
  ];

  const monthlyData: AnalyticsData[] = [
    { week: 'Jan', workouts: 16, duration: 720, calories: 6800 },
    { week: 'Feb', workouts: 18, duration: 810, calories: 7650 },
    { week: 'Mar', workouts: 20, duration: 900, calories: 8500 },
    { week: 'Apr', workouts: 17, duration: 765, calories: 7200 },
  ];

  const currentData = selectedPeriod === 'week' ? weeklyData : monthlyData;
  const maxWorkouts = Math.max(...currentData.map(d => d.workouts));

  const getBarHeight = (value: number): number => {
    return (value / maxWorkouts) * 100;
  };

  const calculateStreak = (): number => {
    return 12;
  };

  const calculateAverage = (): number => {
    const total = currentData.reduce((sum, data) => sum + data.workouts, 0);
    return Math.round(total / currentData.length);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>
          Analytics
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setSelectedPeriod('week')}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
            style={{
              background: selectedPeriod === 'week' 
                ? 'var(--gradient-primary)' 
                : 'var(--gradient-card-bg)',
              color: selectedPeriod === 'week' 
                ? 'var(--color-white)' 
                : 'var(--color-text)',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            Weekly
          </button>
          <button
            onClick={() => setSelectedPeriod('month')}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
            style={{
              background: selectedPeriod === 'month' 
                ? 'var(--gradient-primary)' 
                : 'var(--gradient-card-bg)',
              color: selectedPeriod === 'month' 
                ? 'var(--color-white)' 
                : 'var(--color-text)',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            Monthly
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div 
          className="bg-white rounded-xl p-6"
          style={{ boxShadow: 'var(--shadow-lg)' }}
        >
          <div className="flex items-center space-x-3 mb-4">
            <div 
              className="p-3 rounded-lg"
              style={{ background: 'var(--gradient-primary)' }}
            >
              <Award className="h-6 w-6" style={{ color: 'var(--color-white)' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>
                Current Streak
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                Consecutive days
              </p>
            </div>
          </div>
          <div className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
            {calculateStreak()} days
          </div>
        </div>

        <div 
          className="bg-white rounded-xl p-6"
          style={{ boxShadow: 'var(--shadow-lg)' }}
        >
          <div className="flex items-center space-x-3 mb-4">
            <div 
              className="p-3 rounded-lg"
              style={{ background: 'var(--gradient-secondary)' }}
            >
              <TrendingUp className="h-6 w-6" style={{ color: 'var(--color-white)' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>
                Average
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                Workouts per {selectedPeriod}
              </p>
            </div>
          </div>
          <div className="text-3xl font-bold" style={{ color: 'var(--color-secondary)' }}>
            {calculateAverage()}
          </div>
        </div>

        <div 
          className="bg-white rounded-xl p-6"
          style={{ boxShadow: 'var(--shadow-lg)' }}
        >
          <div className="flex items-center space-x-3 mb-4">
            <div 
              className="p-3 rounded-lg"
              style={{ background: 'var(--gradient-accent)' }}
            >
              <Calendar className="h-6 w-6" style={{ color: 'var(--color-white)' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>
                This {selectedPeriod === 'week' ? 'Week' : 'Month'}
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                Workouts completed
              </p>
            </div>
          </div>
          <div className="text-3xl font-bold" style={{ color: 'var(--color-accent)' }}>
            {currentData[currentData.length - 1]?.workouts || 0}
          </div>
        </div>
      </div>

      <div 
        className="bg-white rounded-xl p-6"
        style={{ boxShadow: 'var(--shadow-lg)' }}
      >
        <div className="flex items-center space-x-2 mb-6">
          <BarChart3 className="h-6 w-6" style={{ color: 'var(--color-primary)' }} />
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>
            Workout Consistency
          </h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-end justify-between h-48 space-x-2">
            {currentData.map((data, index) => (
              <div key={index} className="flex-1 flex flex-col items-center space-y-2">
                <div 
                  className="w-full rounded-t-lg transition-all duration-500 ease-out relative group"
                  style={{
                    height: `${getBarHeight(data.workouts)}%`,
                    background: 'var(--gradient-primary)',
                    minHeight: '8px'
                  }}
                >
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {data.workouts} workouts
                  </div>
                </div>
                <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                  {data.week}
                </span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
            <div className="text-center">
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                Total Workouts
              </p>
              <p className="text-xl font-bold" style={{ color: 'var(--color-text)' }}>
                {currentData.reduce((sum, data) => sum + data.workouts, 0)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                Total Duration
              </p>
              <p className="text-xl font-bold" style={{ color: 'var(--color-text)' }}>
                {Math.round(currentData.reduce((sum, data) => sum + data.duration, 0) / 60)}h
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                Total Calories
              </p>
              <p className="text-xl font-bold" style={{ color: 'var(--color-text)' }}>
                {currentData.reduce((sum, data) => sum + data.calories, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyAnalyticsPanel;
